import { useMutation } from '@urql/vue';

import { useAnalyticsService } from '~/composables/core';
import { EVENTS } from '~/constants/events';
import { requestSchoolVerificationMutation } from '~/graphql/documents/core';
import { getFirstErrorMessageFromCombinedErrors } from '~/utils/graphql';

export type SchoolVerificationRequestSource = 'sidebar' | 'studentsHub';

export const SCHOOL_VERIFICATION_REQUESTED_STATE_KEY = 'schools-verification-requested';

/**
 * An unverified Personal classroom refuses student invites until we verify it. This lets the
 * teacher ask for that verification from any surface; whether the request was sent is shared per
 * school, so every surface showing the offer agrees once one of them sends it.
 */
export const useSchoolVerificationRequest = () => {
  const { t } = useI18n();
  const analytics = useAnalyticsService();
  const { currentSchoolId, currentSchool } = useAuth();

  const awaitingVerification = computed(
    () => !!currentSchool.value?.isPersonalSchool && !currentSchool.value.verified,
  );

  const requestedBySchool = useState<Record<string, boolean>>(SCHOOL_VERIFICATION_REQUESTED_STATE_KEY, () => ({}));
  const requested = computed(() => !!currentSchoolId.value && !!requestedBySchool.value[currentSchoolId.value]);

  const error = ref<string | null>(null);

  const { executeMutation, fetching: requesting } = useMutation(requestSchoolVerificationMutation);

  const request = async (source: SchoolVerificationRequestSource) => {
    const schoolId = currentSchoolId.value;
    if (!schoolId) {
      return;
    }
    analytics.track(EVENTS.SCHOOLS_VERIFICATION_REQUEST_CLICKED, { schoolId, source });
    error.value = null;
    const { error: mutationError } = await executeMutation({ schoolId });
    if (mutationError) {
      error.value = getFirstErrorMessageFromCombinedErrors(t, mutationError);
      return;
    }
    requestedBySchool.value = { ...requestedBySchool.value, [schoolId]: true };
  };

  return { awaitingVerification, request, requesting, requested, error };
};
